/**
 * PÁGINA DE REMATES
 * ============================
 * Galería de remates con estadísticas de lotes y cabezas
 * Separados en remates activos y anteriores
 */

import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Eye, Loader2, Radio, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';
import { api } from '../services/api';

// URL base para las imágenes de portada
const PORTADA_BASE_URL = 'https://plazarural.com.uy/uploads/remates/';

// Mapeo de estados
const ESTADO_MAP = {
  2: { label: 'ACTIVO', color: 'bg-green-500', textColor: 'text-green-600', badgeColor: 'bg-green-100 text-green-700' },
  3: { label: 'EN CURSO', color: 'bg-red-500', textColor: 'text-red-600', badgeColor: 'bg-red-100 text-red-700', animate: true },
  4: { label: 'FINALIZADO', color: 'bg-gray-400', textColor: 'text-gray-600', badgeColor: 'bg-gray-100 text-gray-600' }
};

const RematesAnterioresPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [remates, setRemates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manejar scroll para header
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cargar remates desde la API
  useEffect(() => {
    const fetchRemates = async () => {
      try {
        setLoading(true);
        const data = await api.getRematesList();
        console.log('[RematesAnterioresPage] Remates cargados:', data);
        setRemates(data);
      } catch (error) {
        console.error('[RematesAnterioresPage] Error cargando remates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRemates();
  }, []);

  // Separar remates activos/en curso de finalizados
  const rematesActivos = remates.filter(r => r.estado === 2 || r.estado === 3);
  const rematesAnteriores = remates.filter(r => r.estado === 4);

  // Función para navegar a los lotes del remate
  const handleVerLotes = (numero) => {
    navigate(`/lotes?remate=${numero}`);
  };

  // Formatear número con separador de miles
  const formatNumber = (num) => {
    return num ? num.toLocaleString('es-UY') : '0';
  };

  // Componente de tarjeta de remate reutilizable
  const RemateCard = ({ remate, isActive = false }) => {
    const estadoInfo = ESTADO_MAP[remate.estado] || ESTADO_MAP[4];
    const portadaUrl = remate.portada ? `${PORTADA_BASE_URL}${remate.portada}` : null;

    return (
      <div
        className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border ${isActive ? 'border-green-300 ring-2 ring-green-200' : 'border-gray-200'} group cursor-pointer`}
        onClick={() => handleVerLotes(remate.numero)}
      >
        {/* Imagen de carátula con orientación vertical - altura h-80 para mostrar más */}
        <div className="relative h-80 bg-gradient-to-br from-green-100 to-green-200 overflow-hidden">
          {portadaUrl ? (
            <img
              src={portadaUrl}
              alt={`Remate ${remate.numero}`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}

          {/* Placeholder si no hay imagen */}
          <div
            className={`absolute inset-0 items-center justify-center ${portadaUrl ? 'hidden' : 'flex'}`}
          >
            <div className="text-center">
              <Calendar className="w-20 h-20 text-green-300 mx-auto mb-2" />
              <p className="text-green-400 font-semibold">R{remate.numero}</p>
            </div>
          </div>

          {/* Badge de estado */}
          <div
            className={`absolute top-4 right-4 ${estadoInfo.badgeColor} px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5 ${estadoInfo.animate ? 'animate-pulse' : ''}`}
          >
            {remate.estado === 3 && <Radio className="w-3 h-3" />}
            {estadoInfo.label}
          </div>

          {/* Overlay hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
            <button
              className="bg-white/90 hover:bg-white text-green-600 px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleVerLotes(remate.numero);
              }}
            >
              <Eye className="w-4 h-4" />
              Ver Lotes
            </button>
          </div>
        </div>

        {/* Información del remate */}
        <div className="p-5">
          {/* Header con número y fecha */}
          <div className="flex items-center justify-between mb-4">
            <span className={`${isActive ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-gray-600 to-gray-500'} text-white px-4 py-1.5 rounded-full text-base font-bold shadow-lg`}>
              R{remate.numero}
            </span>
            <span className="text-sm font-semibold text-gray-600">
              {remate.fecha || 'Sin fecha'}
            </span>
          </div>

          {/* Ubicación */}
          {remate.lugar && (
            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span className="text-sm truncate">{remate.lugar}</span>
            </div>
          )}

          {/* Estadísticas */}
          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
            {/* Lotes */}
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {formatNumber(remate.total_lotes)}
              </div>
              <div className="text-xs text-gray-500 font-medium">Lotes</div>
            </div>

            {/* Vacunos */}
            <div className="text-center border-x border-gray-100">
              <div className="text-lg font-bold text-green-600">
                {remate.cabezas_vacunos > 0 ? formatNumber(remate.cabezas_vacunos) : '—'}
              </div>
              <div className="text-xs text-gray-500 font-medium">Vacunos</div>
            </div>

            {/* Ovinos */}
            <div className="text-center">
              <div className={`text-lg font-bold ${remate.cabezas_ovinos > 0 ? 'text-blue-600' : 'text-gray-300'}`}>
                {remate.cabezas_ovinos > 0 ? formatNumber(remate.cabezas_ovinos) : '—'}
              </div>
              <div className="text-xs text-gray-500 font-medium">Ovinos</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      {/* Forzar header con fondo desde el inicio */}
      <ModernHeader scrollY={100} />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header de la página */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Calendar className="w-10 h-10 text-green-600" />
              Remates
            </h1>
            <p className="text-gray-600 text-lg">
              Explora nuestros remates activos y anteriores
            </p>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Cargando remates...</p>
              </div>
            </div>
          )}

          {/* Contenido cuando terminó de cargar */}
          {!loading && (
            <>
              {/* SECCIÓN: Remates Activos / En Curso */}
              {rematesActivos.length > 0 && (
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full">
                      <Clock className="w-5 h-5" />
                      <span className="font-bold text-lg">Próximos Remates</span>
                    </div>
                    <div className="flex-1 h-px bg-green-200"></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {rematesActivos.map(remate => (
                      <RemateCard key={remate.id} remate={remate} isActive={true} />
                    ))}
                  </div>
                </div>
              )}

              {/* SECCIÓN: Remates Anteriores (Finalizados) */}
              {rematesAnteriores.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full">
                      <Calendar className="w-5 h-5" />
                      <span className="font-bold text-lg">Remates Anteriores</span>
                    </div>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {rematesAnteriores.map(remate => (
                      <RemateCard key={remate.id} remate={remate} isActive={false} />
                    ))}
                  </div>
                </div>
              )}

              {/* Mensaje si no hay remates */}
              {remates.length === 0 && (
                <div className="text-center py-16">
                  <Calendar className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">
                    No hay remates disponibles
                  </h3>
                  <p className="text-gray-500">
                    Vuelve pronto para ver los próximos remates
                  </p>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      <ModernFooter />
    </div>
  );
};

export default RematesAnterioresPage;
